import { FormEvent, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, extractError } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { useFormErrors } from '../../hooks/useFormErrors';
import { combine, password as passwordRule, required } from '../../lib/validate';
import styles from './Auth.module.scss';

export default function ResetPasswordPage(): JSX.Element {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { errors, setErrors, clearError } = useFormErrors<'password'>();

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const passErr = combine(required, passwordRule)(password);
    if (passErr) {
      setErrors({ password: passErr });
      return;
    }
    setErrors({});
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/auth/reset-password', { token, password });
      navigate('/conta/login', { replace: true });
    } catch (err) {
      setError(extractError(err, 'Falha ao redefinir palavra-passe'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title={t.auth.resetTitle} noindex />
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <header className={styles.intro}>
            <h1 className={styles.title}>{t.auth.resetTitle}</h1>
            <p className={styles.lead}>Define uma nova palavra-passe para a tua conta.</p>
          </header>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <Field
              label="Nova palavra-passe"
              required
              error={errors.password}
              className={styles.field}
            >
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) clearError('password');
                }}
                autoComplete="new-password"
              />
            </Field>
            {error ? <p className={styles.error}>{error}</p> : null}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting || !token}>
              {submitting ? t.common.loading : t.auth.resetCta}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}
